import { downloadCSV } from "react-admin";
import jsonExport from "jsonexport/dist";

import { formatAttendanceCellLabel, formatAttendanceTimeLocal } from "./attendance-cell-display";
import type { AttendanceEmployeeRow } from "./attendance-types";

export type BuildAttendanceCsvParams = {
  rows: AttendanceEmployeeRow[];
  daysInMonth: number[];
  month: string;
  timezone: string;
  includeLate: boolean;
};

/** Flat rows for CSV export (summary counts + per-day labels). */
export function buildAttendanceCsvRecords({
  rows,
  daysInMonth,
  timezone,
  includeLate,
}: BuildAttendanceCsvParams): Record<string, string | number>[] {
  return rows.map((emp) => {
    const record: Record<string, string | number> = {
      Id: emp.employeeId,
      Name: emp.name,
      Department: emp.department ?? "",
      Present: emp.summary.presentDays,
      Incomplete: emp.summary.incompleteDays,
      Absent: emp.summary.absentDays,
      Leave: emp.summary.leaveDays ?? 0,
      Hours: emp.summary.totalHours,
      WorkingDays: emp.summary.workingDaysInMonth,
    };
    if (includeLate) {
      record.Late = emp.summary.lateDays ?? 0;
    }
    for (const d of daysInMonth) {
      const cell = emp.days[d];
      record[`D${d}`] = formatAttendanceCellLabel(cell, "times", timezone);
    }
    return record;
  });
}

export function exportAttendanceCsv(params: BuildAttendanceCsvParams): void {
  const records = buildAttendanceCsvRecords(params);
  const headers = [
    "Id",
    "Name",
    "Department",
    "Present",
    "Incomplete",
    "Absent",
    "Leave",
    "Hours",
    ...(params.includeLate ? ["Late"] : []),
    "WorkingDays",
    ...params.daysInMonth.map((d) => `D${d}`),
  ];
  jsonExport(records, { headers }, (_err, csv) => {
    downloadCSV(csv, `staff-attendance_${params.month}`);
  });
}

/** Punch list for drill-down / CSV detail helpers. */
export function formatPunchList(punches: string[], timezone: string): string {
  if (!punches.length) return "";
  return punches.map((p) => formatAttendanceTimeLocal(p, timezone)).join("; ");
}
